// App.js
// import { useState, useEffect, useMemo } from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import getEcho from "echo";
// 🔥 Broadcast Channel (GLOBAL)
const broadcastChannel = new BroadcastChannel("echo_channel");
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// MUI
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Core Components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL
import rtlPlugin from "@mui/stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes
import { getRoutes } from "routes";

// Context
import {
  useSoftUIController,
  setMiniSidenav,
} from "context";

// Logo
import brand from "assets/images/logo-ct.png";

// Auth
import { useAuth } from "./AuthContext";
import ProjectLiveChatProvider from "layouts/project-live-chat/ProjectLiveChatProvider";

// Idle tracker
//import IdleTracker from "components/IdleTracker";
import { isUserLoggedIn } from "config";
import LoadingSpinner from "components/LoadingSpinner";
import { hasPermission } from "utils/granularPermissions";

export default function App() {
  const [onBreak, setOnBreak] = useState(false);

  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const subscribedRef = useRef(false);
  // const { isLoggedIn, isAuthReady } = useAuth();
  const { isLoggedIn, isAuthReady, user } = useAuth();
  const [routeVisibility, setRouteVisibility] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("route_visibility")) || {};
    } catch {
      return {};
    }
  });


  /* const rawRole = localStorage.getItem("role");
  const userRoles = useMemo(() => {
    if (!rawRole) return [];
    return rawRole.startsWith("[") ? JSON.parse(rawRole) : [rawRole];
  }, [rawRole]); */


  const rawPerms = localStorage.getItem("permissions");
  const userPermissions = useMemo(() => {
    if (!rawPerms) return [];
    try {
      return JSON.parse(rawPerms);
    } catch {
      return rawPerms.split(",").map((p) => p.trim());
    }
  }, [rawPerms]);
  const canSubscribeVendorProjectsRef = useRef(false);
  canSubscribeVendorProjectsRef.current = hasPermission(
    userPermissions,
    "vendor_projects.pages.list.view"
  );

  const filteredRoutes = useMemo(
    () => getRoutes(userPermissions, routeVisibility),
    [userPermissions, routeVisibility]
  );
  // ============================================
  // 🔥 MASTER → SOCKET SUBSCRIBE
  // ============================================
  useEffect(() => {
    if (!isLoggedIn || !user?.id) {
      subscribedRef.current = false;
      return () => {
        subscribedRef.current = false;
      };
    }

    if (subscribedRef.current) return;
    subscribedRef.current = true;

    const echo = getEcho();
    const actorType = user?.actor_type || "user";

    // ❌ slave tab → no socket
    if (!echo) {
      console.log("👥 SLAVE → listening via BroadcastChannel");

      broadcastChannel.onmessage = (event) => {
        const data = event.data;
        if (data?.__echo_relay_type === "realtime-connected") {
          window.dispatchEvent(new CustomEvent("realtime-connected"));
          return;
        }
        if (data?.__echo_relay_type === "vendor-projects") {
          window.dispatchEvent(
            new CustomEvent("vendor-projects-realtime", { detail: data.payload })
          );
          return;
        }
        if (data?.__echo_relay_type === "project-live-chat") {
          window.dispatchEvent(
            new CustomEvent("project-chat-realtime", { detail: data.payload })
          );
          return;
        }
        if (data?.actor_type && data.actor_type !== actorType) return;

        console.log("📡 SLAVE RECEIVED:", data);

        window.dispatchEvent(new CustomEvent("echo-event", { detail: data }));
      };

      return () => {
        broadcastChannel.onmessage = null;
        subscribedRef.current = false;
      };
    }

    console.log("👑 MASTER → subscribing");

    const channel = echo.private(`admin-notifications.${actorType}.${user.id}`);
    const vendorProjectsChannel = canSubscribeVendorProjectsRef.current
      ? echo.private("vendor-projects")
      : null;

    const handler = (data) => {
      if (data?.actor_type && data.actor_type !== actorType) return;

      console.log("🔥 MASTER EVENT:", data);

      // ✅ send to other tabs
      broadcastChannel.postMessage(data);

      // ✅ ALSO trigger UI for current tab
      window.dispatchEvent(new CustomEvent("echo-event", { detail: data }));
    };

    const projectChatHandler = (data) => {
      broadcastChannel.postMessage({
        __echo_relay_type: "project-live-chat",
        payload: data,
      });
      window.dispatchEvent(new CustomEvent("project-chat-realtime", { detail: data }));
    };

    const vendorProjectsHandler = (data) => {
      broadcastChannel.postMessage({
        __echo_relay_type: "vendor-projects",
        payload: data,
      });
      window.dispatchEvent(new CustomEvent("vendor-projects-realtime", { detail: data }));
    };

    const realtimeConnectedHandler = () => {
      broadcastChannel.postMessage({ __echo_relay_type: "realtime-connected" });
      window.dispatchEvent(new CustomEvent("realtime-connected"));
    };

    const pusherConnection = echo.connector?.pusher?.connection;
    pusherConnection?.bind("connected", realtimeConnectedHandler);
    channel.listen(".NotificationEvent", handler);
    channel.listen(".ProjectChatUpdated", projectChatHandler);
    vendorProjectsChannel?.listen(".VendorProjectsUpdated", vendorProjectsHandler);

    return () => {
      pusherConnection?.unbind("connected", realtimeConnectedHandler);
      channel.stopListening(".NotificationEvent", handler);
      channel.stopListening(".ProjectChatUpdated", projectChatHandler);
      vendorProjectsChannel?.stopListening(".VendorProjectsUpdated", vendorProjectsHandler);
      subscribedRef.current = false;
    };

  }, [user?.actor_type, user?.id, isLoggedIn]);

  useEffect(() => {
    const cache = createCache({ key: "rtl", stylisPlugins: [rtlPlugin] });
    setRtlCache(cache);
  }, []);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [location.pathname]);

  // ✅ Updated Auth Redirect Logic
  useEffect(() => {
    if (!isAuthReady) return;

    const isPublicPage =
      location.pathname.startsWith("/authentication") ||
      location.pathname.startsWith("/newuser") ||
      location.pathname.startsWith("/vendor-feasibility");

    const isRoot = location.pathname === "/";

    if (!isLoggedIn && !isPublicPage) {
      navigate("/authentication/sign-in/illustration", { replace: true });
      return;
    }

    if (isLoggedIn && (isPublicPage || isRoot)) {
      navigate("/dashboards/default", { replace: true });
    }
  }, [isAuthReady, isLoggedIn, location.pathname, navigate]);

  // Keep session-based route visibility current in this tab and across tabs.
  useEffect(() => {
    const syncRouteVisibility = (event) => {
      try {
        if (event?.type === "storage" && event.key !== "route_visibility") return;

        if (event?.detail && typeof event.detail === "object") {
          setRouteVisibility(event.detail);
          return;
        }

        const raw = localStorage.getItem("route_visibility");
        setRouteVisibility(raw ? JSON.parse(raw) || {} : {});
      } catch {
        setRouteVisibility({});
      }
    };

    syncRouteVisibility();
    window.addEventListener("route-visibility-changed", syncRouteVisibility);
    window.addEventListener("storage", syncRouteVisibility);

    return () => {
      window.removeEventListener("route-visibility-changed", syncRouteVisibility);
      window.removeEventListener("storage", syncRouteVisibility);
    };
  }, []);


  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const renderRoutes = (routesList) =>
    routesList.flatMap((route, idx) => {
      if (route.collapse) return renderRoutes(route.collapse);
      if (route.route && route.component) {
        return (
          <Route
            key={route.key || `route-${idx}`}
            path={route.route}
            element={route.component}
          />
        );
      }
      return [];
    });


  // ✅ Safe Guard: Wait for Auth Check First
  if (!isAuthReady) {
    return <LoadingSpinner />; // ⬅️ Use loading spinner instead of plain text
  }

  if (
    !isLoggedIn &&
    !location.pathname.startsWith("/authentication") &&
    !location.pathname.startsWith("/newuser") &&
    !location.pathname.startsWith("/vendor-feasibility")
  ) {
    return <Navigate to="/authentication/sign-in/illustration" replace />;
  }

  const appLayout = (
    <>
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brand}
            brandName="Exchange"
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {/* Floating settings launcher intentionally hidden. */}
        </>
      )}

      {layout === "vr" && <Configurator />}

      <Routes>
        {renderRoutes(filteredRoutes)}
        <Route path="*" element={<Navigate to="/dashboards/default" replace />} />
      </Routes>
    </>
  );


  const appWithLiveChat = isLoggedIn ? (
    <ProjectLiveChatProvider>{appLayout}</ProjectLiveChatProvider>
  ) : (
    appLayout
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {/* {isUserLoggedIn() && <IdleTracker />} */}
        {appWithLiveChat}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* {isUserLoggedIn() && <IdleTracker />} */}
      {appWithLiveChat}
    </ThemeProvider>
  );
}
